import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { MemoryGameState, MemoryObject, MEMORIZATION_TIME, INITIAL_OBJECT_COUNT, GRID_SIZE, GAME_OBJECTS } from './types';

// Helper to generate random objects for the game
const generateObjects = (difficulty: number): MemoryObject[] => {
  const count = INITIAL_OBJECT_COUNT + Math.floor(difficulty / 2); // Increase objects as difficulty increases
  const objects: MemoryObject[] = [];
  const usedPositions = new Set<string>();

  for (let i = 0; i < count; i++) {
    let x: number, y: number;
    do {
      x = Math.floor(Math.random() * GRID_SIZE);
      y = Math.floor(Math.random() * GRID_SIZE);
    } while (usedPositions.has(`${x},${y}`));

    usedPositions.add(`${x},${y}`);
    
    objects.push({
      id: `object-${i}`,
      type: GAME_OBJECTS[i % GAME_OBJECTS.length].type,
      position: { x, y },
      color: GAME_OBJECTS[i % GAME_OBJECTS.length].color,
    });
  }

  return objects;
};

const INITIAL_STATE: MemoryGameState = {
  phase: 'memorization',
  objects: generateObjects(1), // Start with difficulty 1
  playerPlacements: [],
  timeLeft: MEMORIZATION_TIME,
  recallStartTime: undefined,
  levelStartTime: Date.now(), // Track when level started
  difficulty: 1,
  isComplete: false,
  stats: null,
};

type MemoryGameAction =
  | { type: 'START_RECALL' }
  | { type: 'PLACE_OBJECT'; payload: MemoryObject }
  | { type: 'UPDATE_TIME'; payload: number }
  | { type: 'COMPLETE_GAME'; payload: { accuracy: number; timeSeconds: number; score: number; levelTime: number } }
  | { type: 'RESET_GAME'; payload?: { difficulty?: number } };

const memoryGameReducer = (state: MemoryGameState, action: MemoryGameAction): MemoryGameState => {
  switch (action.type) {
    case 'START_RECALL':
      return {
        ...state,
        phase: 'recall',
        timeLeft: 0,
        recallStartTime: Date.now(),
        playerPlacements: [],
      };

    case 'PLACE_OBJECT':
      const newPlacements = [...state.playerPlacements, action.payload];
      const isComplete = newPlacements.length === state.objects.length;

      if (isComplete) {
        // Calculate accuracy by comparing each placed object with the correct original position
        const correctPlacements = newPlacements.filter((placement) => {
          // Find the original object with the same type
          const originalObject = state.objects.find(obj => obj.type === placement.type);
          // Check if the placement is in the correct position
          return originalObject && 
                 placement.position.x === originalObject.position.x && 
                 placement.position.y === originalObject.position.y;
        });

        const accuracy = (correctPlacements.length / state.objects.length) * 100;
        
        // Calculate actual time taken for recall phase
        const recallTimeSeconds = state.recallStartTime 
          ? Math.round((Date.now() - state.recallStartTime) / 1000)
          : 0;
        
        // Calculate total level time from start to completion
        const levelTimeSeconds = state.levelStartTime 
          ? Math.round((Date.now() - state.levelStartTime) / 1000)
          : 0;
        
        const timeBonus = Math.max(0, 300 - recallTimeSeconds); // Less time = higher bonus
        const score = Math.round(accuracy * (1 + timeBonus / 100));

        return {
          ...state,
          playerPlacements: newPlacements,
          phase: 'result',
          isComplete: true,
          stats: {
            timestamp: new Date().toISOString(),
            accuracy,
            timeSeconds: recallTimeSeconds,
            score,
            difficulty: state.difficulty,
            levelTime: levelTimeSeconds,
          },
        };
      }

      return {
        ...state,
        playerPlacements: newPlacements,
      };

    case 'UPDATE_TIME':
      const newTimeLeft = action.payload;
      if (newTimeLeft <= 0 && state.phase === 'memorization') {
        return {
          ...state,
          phase: 'recall',
          timeLeft: 0,
          recallStartTime: Date.now(),
          playerPlacements: [],
        };
      }
      return {
        ...state,
        timeLeft: Math.max(0, action.payload),
      };

    case 'RESET_GAME':
      const newDifficulty = action.payload?.difficulty || state.difficulty;
      const objectCount = INITIAL_OBJECT_COUNT + (newDifficulty - 1) * 2;
      
      return {
        ...INITIAL_STATE,
        difficulty: newDifficulty,
        objects: generateObjects(objectCount),
        timeLeft: MEMORIZATION_TIME,
        recallStartTime: undefined,
        levelStartTime: Date.now(), // Reset level timer
      };

    default:
      return state;
  }
};

const MemoryGameContext = createContext<{
  state: MemoryGameState;
  dispatch: React.Dispatch<MemoryGameAction>;
} | null>(null);

export const MemoryGameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(memoryGameReducer, INITIAL_STATE);

  return (
    <MemoryGameContext.Provider value={{ state, dispatch }}>
      {children}
    </MemoryGameContext.Provider>
  );
};

export const useMemoryGame = () => {
  const context = useContext(MemoryGameContext);
  if (!context) {
    throw new Error('useMemoryGame must be used within a MemoryGameProvider');
  }
  return context;
};